'use client';
import React, { Fragment, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@apollo/client';

import Header from '../../components/Header';
import Load from '../../components/Load';
import TournamentFilters, {
  TournamentFiltersState,
} from '../../components/admin/TournamentFilters';
import TournamentTable from '../../components/admin/TournamentTable';
import { Tournament } from '../../components/admin/TournamentTableRow';
import {
  TORNEIOS_FILTRADOS,
  TorneiosFiltradosData,
  TorneiosFiltradosVars,
  ativarTorneio,
  inativarTorneio
} from '../../services/torneioService';
import { StatusTorneio } from '../../utils/StatusTorneio';
import CreateTournamentDialog from '../../components/admin/CreateTournamentDialog';
import { downloadRelatorioInscricoesCsv } from '../../services/inscricaoTorneioService';
import { Alert, Snackbar } from '@mui/material';

const PAGE_SIZE = 50;

const mapStatus = (status: string): StatusTorneio | null => {
  switch (status) {
    case 'active':
      return StatusTorneio.INSCRICOES_ABERTAS;
    case 'inactive':
      return StatusTorneio.ENCERRADO;
    default:
      return null;
  }
};

const Tournaments: React.FC = () => {
  const navigate = useNavigate();
  const [filters, setFilters] = useState<TournamentFiltersState>({
    name: '',
    startDate: '',
    endDate: '',
    state: '',
    city: '',
    status: '',
  });
  const [tournaments, setTournaments] = useState<Tournament[]>([]);

  const [openDialog, setOpenDialog] = useState(false);
  
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({ open: false, message: '', severity: 'success' });
  const handleCloseSnackbar = () => setSnackbar((s) => ({ ...s, open: false }));

  const { data, loading, error, refetch } = useQuery<
    TorneiosFiltradosData,
    TorneiosFiltradosVars
  >(TORNEIOS_FILTRADOS, {
    variables: {
      cidade: null,
      data: null,
      status: null,
      page: 0,
      size: PAGE_SIZE,
      sortBy: 'dataInicio',
      direction: 'ASC',
    },
    fetchPolicy: 'no-cache',
  });

  useEffect(() => {
    refetch({
      cidade: filters.city || null,
      data: filters.startDate || null,
      status: mapStatus(filters.status),
      page: 0,
      size: PAGE_SIZE,
      sortBy: 'dataInicio',
      direction: 'ASC',
    });
  }, [filters, refetch]);

  useEffect(() => {
    if (data?.torneiosFiltrados.items) {
      const mapped = data.torneiosFiltrados.items.map((t) => ({
        id: t.id,
        name: t.nome,
        avatar: t.avatarUrl || '',
        city: t.local?.endereco?.cidade?.nome ?? '',
        state: t.local?.endereco?.cidade?.uf ?? '',
        startDate: t.dataInicio,
        active: t.ativo ?? false,
      }));
      setTournaments(mapped);
    }
  }, [data]);

  const stateOptions = useMemo(
    () => Array.from(new Set(tournaments.map((t) => t.state).filter(Boolean))),
    [tournaments],
  );

  const cityOptions = useMemo(
    () => Array.from(new Set(tournaments.map((t) => t.city).filter(Boolean))),
    [tournaments],
  );

  const filteredTournaments = useMemo(() => {
    return tournaments.filter((t) => {
      const matchesName = t.name.toLowerCase().includes(filters.name.toLowerCase());
      const matchesState = !filters.state || t.state === filters.state;
      const matchesCity = !filters.city || t.city === filters.city;
      const matchesStatus =
        !filters.status || (filters.status === 'active' ? t.active : !t.active);
      const matchesStartDate =
        !filters.startDate ||
        new Date(t.startDate) >= new Date(filters.startDate);
      const matchesEndDate =
        !filters.endDate ||
        new Date(t.startDate) <= new Date(filters.endDate);

      return (
        matchesName &&
        matchesState &&
        matchesCity &&
        matchesStatus &&
        matchesStartDate &&
        matchesEndDate
      );
    });
  }, [tournaments, filters]);

  const handleFilterChange = (f: TournamentFiltersState) => setFilters(f);

  const handleToggle = (id: string) => {
    if (!id) {
      console.error('ID do torneio é inválido');
      return;
    }
    let gamesAnterior = tournaments.find((t) => t.id === id)?.active;
    if (gamesAnterior === undefined) {
      gamesAnterior = true;
    }

    if (gamesAnterior) {
      inativarTorneio(id).then(() => {
        setTournaments((prev) =>
          prev.map((t) => (t.id === id ? { ...t, active: false } : t)),
        );
        setSnackbar({ open: true, message: 'Torneio inativado com sucesso', severity: 'success' });
      }).catch((error) => {
        console.error('Erro ao inativar torneio:', error);
        setSnackbar({ open: true, message: 'Erro ao inativar torneio', severity: 'error' });
      });
    } else {  
      ativarTorneio(id).then(() => {
        setTournaments((prev) =>
          prev.map((t) => (t.id === id ? { ...t, active: true } : t)),
        );
        setSnackbar({ open: true, message: 'Torneio ativado com sucesso', severity: 'success' });
      }).catch((error) => {
        console.error('Erro ao ativar torneio:', error);
        setSnackbar({ open: true, message: 'Erro ao ativar torneio', severity: 'error' });
      });
    }
  };

  const handleEdit = (id: string) => {
    if (!id) {
      console.error('ID do torneio é inválido');
      return;
    }
    window.location.href = `/admin/edittournament/${id}`;
  };

  const handleView = (id: string) => {
    if (!id) {
      console.error('ID do torneio é inválido');
      return;
    }
    window.location.href = `/admin/tournamentfeed/${id}`;
  };

  const handlePayments = (id: string) => {
    if (!id) {
      console.error('ID do torneio invalido');
      return;
    }

    navigate(`/admin/payments?tournamentId=${encodeURIComponent(id)}`);
  };

  const handleReportsInscriptions = async (id: string) => {
    console.log('reports inscriptions', id);
    try {
      await downloadRelatorioInscricoesCsv({
        torneioId: id || undefined,
        status: (filters.status as any) || undefined,
      });
    } catch (error) {
      console.error('Erro ao baixar relatório de inscrições:', error);
    }
  };

  const handleReportsFinancial = (id: string) => {
    console.log('reports financial', id);
  };

  const handleOpenModal = () => setOpenDialog(true);
  const handleCloseModal = () => setOpenDialog(false);

  return (
    <Fragment>
      <div className="main-wrapper">
        <Header />
        <div className="main-content theme-dark-bg right-chat-active">
          <div className="middle-sidebar-bottom">
            <div className="middle-sidebar-left">
              <div className="middle-wrap">
                <div className="card w-100 border-0 bg-white shadow-xs p-0 mb-4">
                  <div className="card-body p-4 w-100 bg-current border-0 d-flex rounded-3 align-items-center">
                    <button
                      type="button"
                      className="btn btn-link text-white p-0 d-inline-block mt-2 me-4"
                      onClick={() => window.history.back()}
                    >
                      <i className="ti-arrow-left font-sm"></i>
                    </button>
                    <h4 className="font-xs text-white fw-600 mb-0 mt-2 flex-grow-1">Torneios</h4>
                    <button className="btn btn-primary" onClick={handleOpenModal} data-testid="tournament-create-open">
                      Criar Novo Torneio
                    </button>
                  </div>
                </div>

                <TournamentFilters
                  filters={filters}
                  states={stateOptions}
                  cities={cityOptions}
                  onChange={handleFilterChange}
                />

                {loading && <Load />}
                {error && (
                  <p className="text-danger">Erro ao carregar torneios.</p>
                )}
                {!loading && !error && (
                  <TournamentTable
                    tournaments={filteredTournaments}
                    onToggle={handleToggle}
                    onEdit={handleEdit}
                    onView={handleView}
                    onPayments={handlePayments}
                    onReportsInscriptions={handleReportsInscriptions}
                    onReportsFinancial={handleReportsFinancial}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      <CreateTournamentDialog
        open={openDialog}
        onClose={handleCloseModal}
      />
    </Fragment>
  );
};

export default Tournaments;
